import { ComponentProps, HTMLAttributes, Ref } from 'react';
import { DayPicker, getDefaultClassNames } from 'react-day-picker';

import { mergeClasses } from '@/utils';

import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@/assets/icons';
import { Button, BUTTON_VARIANTS } from '@/components/common';

import {
  CalendarDayButton,
  CustomFooter,
  MonthsDropdown,
  YearsDropdown,
} from '@/components/common/Calendar/components';

const Calendar = ({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  buttonColor = 'neutral',
  buttonStyle = 'outline',
  formatters,
  components,
  ...props
}: ComponentProps<typeof DayPicker> & {
  buttonColor?: ComponentProps<typeof Button>['buttonColor'];
  buttonStyle?: ComponentProps<typeof Button>['buttonStyle'];
}) => {
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={mergeClasses(
        'bg-neutral-0 dark:!bg-neutral-10 dark:!border-neutral-9 dark:!rounded-[6px] group/calendar px-6 py-4 [--cell-size:--spacing(8)] [[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent',
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className,
      )}
      captionLayout={captionLayout}
      formatters={{
        formatWeekdayName: (weekday) => weekday.toLocaleString('default', { weekday: 'short' }),
        ...formatters,
      }}
      classNames={{
        root: mergeClasses('w-fit', defaultClassNames.root),
        months: mergeClasses('flex gap-4 flex-col md:flex-row relative', defaultClassNames.months),
        month: mergeClasses('flex flex-col w-full gap-4', defaultClassNames.month),
        nav: mergeClasses(
          'flex items-center gap-1 absolute right-0 top-0 justify-end [&_button]:p-none [&_button]:!min-w-8 [&_button]:!min-h-8 [&_button]:w-8 [&_button]:h-8 [&_button:hover]:bg-neutral-3',
          defaultClassNames.nav,
        ),
        button_previous: mergeClasses(
          BUTTON_VARIANTS({ buttonColor: buttonColor, style: buttonStyle }),
          'border-none !shadow-none aria-disabled:opacity-50 p-0 select-none focus:!ring-0 focus:!outline-none focus-visible:!ring-0 focus-visible:!outline-none',
          defaultClassNames.button_previous,
        ),
        button_next: mergeClasses(
          BUTTON_VARIANTS({ buttonColor: buttonColor, style: buttonStyle }),
          'border-none !shadow-none aria-disabled:opacity-50 p-0 select-none focus:!ring-0 focus:!outline-none focus-visible:!ring-0 focus-visible:!outline-none',
          defaultClassNames.button_next,
        ),
        month_caption: mergeClasses(
          'flex items-center justify-center h-(--cell-size) w-full px-(--cell-size)',
          defaultClassNames.month_caption,
        ),
        dropdowns: mergeClasses(
          'w-full flex items-center text-sm font-medium h-(--cell-size) gap-1',
          defaultClassNames.dropdowns,
        ),
        dropdown_root: mergeClasses(
          'relative has-focus:border-ring border border-input shadow-xs has-focus:ring-ring/50 has-focus:ring-[3px] rounded-md',
          defaultClassNames.dropdown_root,
        ),
        dropdown: mergeClasses('absolute bg-popover inset-0 opacity-0', defaultClassNames.dropdown),
        caption_label: mergeClasses(
          'select-none font-medium',
          captionLayout === 'label'
            ? 'text-sm'
            : 'rounded-md pl-2 pr-1 flex items-center gap-1 text-sm h-8 [&>svg]:text-neutral-7 [&>svg]:size-3.5',
          defaultClassNames.caption_label,
        ),
        table: 'w-full border-collapse',
        weekdays: mergeClasses('flex', defaultClassNames.weekdays),
        weekday: mergeClasses(
          'text-neutral-7 rounded-md flex-1 font-medium text-body-sm select-none',
          defaultClassNames.weekday,
        ),
        week: mergeClasses('flex w-full mt-2', defaultClassNames.week),
        week_number_header: mergeClasses(
          'select-none w-(--cell-size)',
          defaultClassNames.week_number_header,
        ),
        week_number: mergeClasses(
          'text-body-sm select-none text-neutral-7',
          defaultClassNames.week_number,
        ),
        day: mergeClasses(
          'relative w-full h-full p-0 text-center group/day aspect-square select-none dark:[&_button]:text-neutral-0',
          defaultClassNames.day,
        ),
        range_start: mergeClasses(
          'shadow-[inset_-20px_0_0_0_#e9ecef] dark:shadow-[inset_-20px_0_0_0_#272B30] dark:[&_button]:!bg-primary-base',
          defaultClassNames.range_start,
        ),
        range_middle: mergeClasses(
          'rounded-none dark:[&_button]:!bg-neutral-9',
          defaultClassNames.range_middle,
        ),
        range_end: mergeClasses(
          'shadow-[inset_20px_0_0_0_#e9ecef] dark:shadow-[inset_20px_0_0_0_#272B30] dark:[&_button]:!bg-primary-base',
          defaultClassNames.range_end,
        ),
        today: mergeClasses(
          '[&_button]:border [&_button]:border-solid [&_button]:!border-neutral-4 data-[selected=true]:rounded-none',
          defaultClassNames.today,
        ),
        outside: mergeClasses(
          '[&_button]:text-neutral-6 dark:[&_button]:!text-neutral-7 aria-selected:text-neutral-7',
          defaultClassNames.outside,
        ),
        disabled: mergeClasses('text-neutral-7 opacity-50', defaultClassNames.disabled),
        hidden: mergeClasses('invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({
          className,
          rootRef,
          ...props
        }: {
          rootRef?: Ref<HTMLDivElement> | undefined;
        } & HTMLAttributes<HTMLDivElement>) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={mergeClasses(className)}
              {...props}
            />
          );
        },
        Chevron: ({
          className,
          orientation,
          ...props
        }: {
          className?: string | undefined;
          size?: number | undefined;
          disabled?: boolean | undefined;
          orientation?: 'left' | 'right' | 'up' | 'down' | undefined;
        }) => {
          if (orientation === 'left') {
            return (
              <ChevronLeftIcon
                className={mergeClasses('size-6 text-primary-base', className)}
                {...props}
              />
            );
          }

          if (orientation === 'right') {
            return (
              <ChevronRightIcon
                className={mergeClasses('size-6 text-primary-base', className)}
                {...props}
              />
            );
          }

          return (
            <ChevronDownIcon
              className={mergeClasses('size-6 text-primary-base', className)}
              {...props}
            />
          );
        },
        DayButton: CalendarDayButton,
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="flex size-(--cell-size) items-center justify-center text-center">
                {children}
              </div>
            </td>
          );
        },
        YearsDropdown: YearsDropdown,
        MonthsDropdown: MonthsDropdown,
        Footer: CustomFooter,
        ...components,
      }}
      {...props}
    />
  );
};

export { Calendar };
